import { useState } from 'react'
import _ from 'lodash'
import { Bar, BarChart, ReferenceLine, ResponsiveContainer, XAxis, YAxis } from 'recharts'
import { useTranslation } from 'react-i18next'

import { Link } from '../navigation'
import { colors } from '../data'
import { ChartContainer } from '../cockpit/Graphs'
import { useRoles } from '../user/Auth'

import IpRightsProvider from './interfaces/IpRightsProvider'
import RenewalsProvider from './interfaces/RenewalsProvider'
import { useRenewals } from './interfaces/Renwals'
import { useIpRights } from './interfaces/IpRights'
import { useSettings } from './interfaces/Settings'
import { Renewal } from './interfaces/renewal_types'

const FEE_OFFSET_DAYS = 14
const PAST_MONTHS = 6
const FUTURE_MONTHS = 6
const AXIS_COLOR = '#666'

type EnrichedRenewal = Renewal & { reference?: string }

type Bucket = {
    key: string
    start: Date
    end: Date
    label: string
    yearLabel: string
    active: number
    planned: number
    activeRenewals: EnrichedRenewal[]
    plannedRenewals: EnrichedRenewal[]
}

export default function RenewalsPipelineChart() {
    const { hasAnnuities, hasNewAnnuities } = useRoles()
    const { implementation } = useSettings()
    if (!hasAnnuities && !hasNewAnnuities) return null

    if (implementation === 'none') {
        return <ActualRenewalsPipelineChart />
    }

    const today = new Date()
    const start = new Date(today.getFullYear(), today.getMonth() - PAST_MONTHS, 1)
    const end = new Date(today.getFullYear(), today.getMonth() + FUTURE_MONTHS + 1, 1)
    const startStr = start.toISOString().slice(0, 10)
    const endStr = end.toISOString().slice(0, 10)

    return (
        <IpRightsProvider>
            <RenewalsProvider start={startStr} end={endStr} states={['Open']}>
                <ActualRenewalsPipelineChart />
            </RenewalsProvider>
        </IpRightsProvider>
    )
}

function ActualRenewalsPipelineChart() {
    const { t } = useTranslation()
    const { renewals } = useRenewals()
    const { ipRightById } = useIpRights()

    const enriched: EnrichedRenewal[] = renewals.map(r => ({
        ...r,
        reference: ipRightById[r.ipRightId]?.clientReference,
    }))

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const buckets: Bucket[] = _.range(-PAST_MONTHS, FUTURE_MONTHS).map(i => {
        const start = new Date(today.getFullYear(), today.getMonth() + i, 1)
        const end = new Date(today.getFullYear(), today.getMonth() + i + 1, 1)
        const label = start.toLocaleDateString(undefined, { month: 'long' }).charAt(0)
        const isFirst = i === -PAST_MONTHS
        const yearLabel = (isFirst || start.getMonth() === 0) ? `'${String(start.getFullYear()).slice(-2)}` : ''
        const key = `m${i + PAST_MONTHS}`
        return { key, start, end, label, yearLabel, active: 0, planned: 0, activeRenewals: [], plannedRenewals: [] }
    })

    for (const r of enriched) {
        if (!r.feeDueDate) continue
        const fee = new Date(r.feeDueDate)
        const effective = new Date(fee.getTime() - FEE_OFFSET_DAYS * 24 * 60 * 60 * 1000)
        const bucket = buckets.find(b => effective >= b.start && effective < b.end)
        if (!bucket) continue
        const instruction = r.instructionDueDate ? new Date(r.instructionDueDate) : undefined
        const inWindow = instruction !== undefined && today >= instruction && today < effective
        if (inWindow) {
            bucket.active++
            bucket.activeRenewals.push(r)
        } else {
            bucket.planned++
            bucket.plannedRenewals.push(r)
        }
    }

    const data = buckets.map(b => ({
        key: b.key,
        label: b.label,
        yearLabel: b.yearLabel,
        active: b.active,
        planned: b.planned,
    }))

    const [hover, setHover] = useState<{ index: number, category: 'active' | 'planned' } | null>(null)

    const renderTick = (props: any) => {
        const { x, y, index } = props
        const point = data[index]
        if (!point) return null
        return (
            <g transform={`translate(${x},${y})`}>
                <text x={0} y={14} textAnchor="middle" fill="#666" fontSize={12}>{point.label}</text>
                {point.yearLabel && <text x={0} y={28} textAnchor="middle" fill="#666" fontSize={11}>{point.yearLabel}</text>}
            </g>
        )
    }

    const hoveredBucket = hover ? buckets[hover.index] : null
    const hoveredItems = hover && hoveredBucket
        ? (hover.category === 'active' ? hoveredBucket.activeRenewals : hoveredBucket.plannedRenewals)
        : []
    const hoveredColor = hover?.category === 'active' ? '#f87171' : colors['pcx-300']

    return (
        <ChartContainer panelType="Multiple" panelId={2} panelName="Renewals pipeline">
            <h2 className='mb-4'>
                <Link to="/_renewals/notices">{t('cockpit-page.renewals-pipeline')}</Link>
            </h2>
            <div className="relative">
                <ResponsiveContainer className='text-sm' width="100%" height={200}>
                    <BarChart data={data}>
                        <XAxis dataKey="key" interval={0} tick={renderTick} height={40} />
                        <YAxis allowDecimals={false} />
                        <ReferenceLine x={`m${PAST_MONTHS}`} stroke={AXIS_COLOR} strokeWidth={2} ifOverflow="extendDomain" />
                        <Bar
                            name="Instruction due passed" dataKey="active" stackId="a" fill={'#f87171'}
                            onMouseEnter={(_d: any, idx: number) => setHover({ index: idx, category: 'active' })}
                            onMouseLeave={() => setHover(null)}
                        />
                        <Bar
                            name="Planned" dataKey="planned" stackId="a" fill={colors['pcx-300']}
                            onMouseEnter={(_d: any, idx: number) => setHover({ index: idx, category: 'planned' })}
                            onMouseLeave={() => setHover(null)}
                        />
                    </BarChart>
                </ResponsiveContainer>
                {hover && hoveredItems.length > 0 && (
                    <div
                        className="absolute bg-white border border-pcx-300 rounded shadow p-2 text-xs z-10 pointer-events-none"
                        style={{ left: 60, top: 8, maxWidth: '40rem' }}
                    >
                        {hoveredItems.map((r, i) => <RenewalLine key={i} renewal={r} color={hoveredColor} />)}
                    </div>
                )}
            </div>
            <div className="flex flex-col gap-2 text-slate-500 text-xs w-full pl-2 mt-2">
                <div className="inline-flex gap-2 items-center"><div className="h-4 w-4" style={{ background: '#f87171' }} /> {t('cockpit-page.instruction-due-passed')}</div>
                <div className="inline-flex gap-2 items-center"><div className="h-4 w-4" style={{ background: colors['pcx-300'] }} /> {t('cockpit-page.planned-renewals')}</div>
            </div>
        </ChartContainer>
    )
}

function RenewalLine({ renewal, color }: { renewal: EnrichedRenewal, color?: string }) {
    const ref = renewal.reference || '-'
    const annuity = renewal.annuity ? `Annuity ${renewal.annuity}` : 'Renewal'
    const text = `${ref}: ${annuity}`
    return (
        <div
            className="leading-snug whitespace-nowrap overflow-hidden text-ellipsis"
            title={text}
            style={color ? { color } : undefined}
        >
            {text}
        </div>
    )
}
